import React, {useContext, useEffect, useState} from "react";
import { useLocation } from 'react-router-dom';
import {FaEye, FaEyeSlash, FaLock, FaRegUser} from "react-icons/fa";
import {MdOutlineEmail} from "react-icons/md";
import {authAPI} from '../../Api/api';
import CustomModal from '../../Component/UI/CustomModal/customModal';
import Button from '../../Component/UI/Button/Button';
import GoogleButton from "./GoogleButton/GoogleButton";
import { getGoogleAuthRedirectError } from '../../Utils/googleAuthError';
import LanguageContext from '../../Context/LanguageContext';
import { useTranslation } from 'react-i18next';

function Register({navigate}) {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const [formData, setFormData] = useState({name: '', email: '', password: ''});
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isSuccessPopupOpen, setSuccessPopupOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [errors, setErrors] = useState({});
    const location = useLocation();

    // Show errors from a failed Google sign-up redirect (e.g. account already exists).
    useEffect(() => {
        const urlError = getGoogleAuthRedirectError(location.search, t);
        if (urlError) {
            setErrorMessage(urlError);
        }
    }, [location.search, t]);

    const calculatePasswordStrength = (password) => {
        if (!password) return {strength: 0, text: '', color: 'var(--password-meter-empty)'};

        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/\d/.test(password)) strength += 12.5;
        if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5;

        if (strength <= 25) return {strength, text: t('registerPage.weak'), color: 'var(--password-meter-weak)'};
        if (strength <= 50) return {strength, text: t('registerPage.fair'), color: 'var(--password-meter-fair)'};
        if (strength <= 75) return {strength, text: t('registerPage.good'), color: 'var(--password-meter-good)'};
        return {strength, text: t('registerPage.strong'), color: 'var(--password-meter-strong)'};
    };

    const passwordStrength = calculatePasswordStrength(formData.password);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('registerPage.nameRequired');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('registerPage.nameMinLength');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('registerPage.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('registerPage.emailInvalid');
        }

        if (!formData.password.trim()) {
            newErrors.password = t('registerPage.passwordRequired');
        } else if (formData.password.length < 8) {
            newErrors.password = t('registerPage.passwordMinLength');
        }

        if (!agreeToTerms) {
            newErrors.terms = t('registerPage.termsRequired');
        }

        setErrors(newErrors);
        return newErrors;
    };


    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        const nextValue = name === 'name' && value.length > 0
            ? value.charAt(0).toUpperCase() + value.slice(1)
            : value;

        setFormData({...formData, [name]: nextValue});
        setErrorMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) return;

        try {
            const response = await authAPI.register(formData);
            setSuccessMessage(response.data.message || t('registerPage.registrationSuccessful'));
            setSuccessPopupOpen(true)
            setTimeout(() => {
                setSuccessPopupOpen(false);
                navigate(`/${lang}/sign-in`);
            }, 2000);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || t('registerPage.registrationFailed'));
        }
    };

    const handleGoogleAuth = () => {
        try {
            window.location.href = authAPI.googleAuth({ intent: 'register' });
        } catch (err) {
            setErrorMessage(err.message || t('registerPage.registrationFailed'));
        }
    };

    const reverseClass = dir() === 'rtl' ? 'reverse' : '';

    return (
        <main className='auth-main' lang={['fa', 'ps', 'ar'].includes(lang) ? lang : 'en'}>
            <section className='section auth-public-section'>
                <div className='register-container container grid'>

                    <CustomModal
                        isModalOpen={isSuccessPopupOpen}
                        setIsModalOpen={setSuccessPopupOpen}
                    >
                        <h1 className='custom-modal-successful-title'>
                            {t('registerPage.created')}
                        </h1>
                        <p className='custom-modal-description'>
                            {successMessage}
                        </p>
                        <Button
                            type="button"
                            onClick={() => setSuccessPopupOpen(false)}
                        >
                            {t('registerPage.ok')}
                        </Button>
                    </CustomModal>

                    <form className="loginForm" onSubmit={handleSubmit}>
                        <h1 className="account-form-title">
                            {t('registerPage.signUp')}
                        </h1>

                        <GoogleButton onClick={handleGoogleAuth}/>

                        <div className="loginContent">
                            <div>
                                <div className={`loginBox ${reverseClass}`}>
                                    <FaRegUser/>
                                    <div className="loginBoxInput">
                                        <input
                                            name="name"
                                            type="text"
                                            className="loginInput"
                                            placeholder=" "
                                            value={formData.name}
                                            onChange={handleChange}
                                        />
                                        <label className="loginLabel">
                                            {t('registerPage.name')}
                                        </label>
                                    </div>
                                </div>
                                {errors.name && <p className="auth-error">{errors.name}</p>}
                            </div>

                            <div>
                                <div className={`loginBox ${reverseClass}`}>
                                    <MdOutlineEmail/>
                                    <div className="loginBoxInput">
                                        <input
                                            placeholder=" "
                                            name="email"
                                            type="email"
                                            className="loginInput"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                        <label className="loginLabel">
                                            {t('registerPage.email')}
                                        </label>
                                    </div>
                                </div>
                                {errors.email && <p className='auth-error'>{errors.email}</p>}
                            </div>

                            <div>
                                <div className={`loginBox ${reverseClass}`}>
                                    <FaLock className='loginIcon'/>
                                    <div className="loginBoxInput">
                                        <input
                                            placeholder=" "
                                            name="password"
                                            type={passwordVisible ? 'text' : 'password'}
                                            className="loginInput"
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                        <label className="loginLabel">
                                            {t('registerPage.password')}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={togglePasswordVisibility}
                                            aria-label={passwordVisible ? t('registerPage.hidePassword') : t('registerPage.showPassword')}
                                        >
                                            {passwordVisible ? <FaEyeSlash className='a-eye-icon'/> :
                                                <FaEye className='a-eye-icon'/>}
                                        </button>
                                    </div>
                                </div>
                                {errors.password && <p className='auth-error'>{errors.password}</p>}
                            </div>


                            {formData.password && (
                                <div className="password-strength">
                                    <div className="password-strength-bar">
                                        <div
                                            className="password-strength-fill"
                                            style={{
                                                width: `${passwordStrength.strength}%`,
                                                backgroundColor: passwordStrength.color
                                            }}
                                        />
                                    </div>
                                    <p
                                        className="password-strength-text"
                                        style={{color: passwordStrength.color}}
                                    >
                                        {t('registerPage.passwordStrengthLabel')}: {passwordStrength.text}
                                    </p>
                                </div>
                            )}


                            <div className="auth-terms">
                                <label className="auth-terms-label" dir={dir()}>
                                    <input
                                        type="checkbox"
                                        checked={agreeToTerms}
                                        onChange={(e) => {
                                            setAgreeToTerms(e.target.checked);
                                            setErrors({...errors, terms: ''});
                                        }}
                                        className="auth-terms-checkbox"
                                    />
                                    <span className="auth-terms-text">
                                        {t('registerPage.agreeToTermsPrefix')} {' '}
                                        <span className="auth-terms-link">{t('registerPage.termsOfService')}</span>
                                        {' '}{t('registerPage.and')}{' '}
                                        <span className="auth-terms-link">{t('registerPage.privacyPolicy')}</span>
                                    </span>
                                </label>

                                {errors.terms && (
                                    <p className="auth-terms-error">{errors.terms}</p>
                                )}
                            </div>

                        </div>
                        <div>
                            <Button type='submit' className='loginButton'>
                                {t('registerPage.register')}
                            </Button>
                        </div>

                        <p className="loginRegister">
                            {t('registerPage.alreadyHaveAccount')}
                            <strong
                                className='auth-link'
                                onClick={() => navigate(`/${lang}/sign-in`)}
                            >
                                {t('registerPage.signIn')}
                            </strong>
                        </p>

                        {errorMessage && <p className="auth-form-error" role="alert">{errorMessage}</p>}
                    </form>
                </div>
            </section>
        </main>
    );
}

export default Register;
